import { useCallback, useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../components/ui/Badge/index.js';
import { Button } from '../../components/ui/Button/index.js';
import { Input } from '../../components/ui/Input/index.js';
import { useToast } from '../../components/ui/Toast/index.js';
import { ErrorState } from '../../components/feedback/ErrorState/index.js';
import {
  normalizeThemeTitleText,
  ThemeTitleEditor,
  toConfirmedThemeTitle,
  toConfirmedThemeTitles,
  useThemeTitleRows,
} from '../../components/features/sns-content-generator/ThemeTitleEditor/index.js';
import { ThemeTitleConfirmList } from '../../components/features/sns-content-generator/ThemeTitleConfirmList/index.js';
import { GeneratedImageGallery } from '../../components/features/sns-content-generator/GeneratedImageGallery/index.js';
import {
  getDefaultImagePageCount,
  isKnownSnsContentGeneratorAccountId,
} from '../../components/features/sns-content-generator/accounts.js';
import {
  MAX_IMAGE_PAGE_COUNT,
  MIN_IMAGE_PAGE_COUNT,
  useConfirmedThemeTitles,
  useConnectTiktok,
  useGenerateCaption,
  useGenerateContentImages,
  useTiktokAuthUrl,
} from '../../hooks/useSnsContentGenerator.js';
import type {
  ConfirmedThemeTitle,
  GeneratedContentImages,
  GeneratedThemeTitle,
} from '../../hooks/useSnsContentGenerator.js';
import { TIKTOK_CONNECT_WINDOW_NAME } from './tiktok/callback.js';
import type { TiktokCallbackMessage } from './tiktok/callback.js';

/** キャプションAPIの`{postText, hashtags}`を、編集可能な1つのテキストへまとめる。 @graph-connects none */
function combineCaptionText(postText: string, hashtags: string[]): string {
  return `${postText}\n\n${hashtags.join(' ')}`;
}

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business SNSコンテンツ生成のアカウント別ルート。URLの `$accountId` を検証し、
 * 未知のIDなら案内表示にフォールバックする。有効なIDなら実画面（SnsContentGeneratorIndexPage）を描画する
 * @graph-connects SnsContentGeneratorIndexPage [calls] 実画面コンポーネント描画
 */
export const Route = createFileRoute('/sns-content-generator/$accountId')({
  component: SnsContentGeneratorAccountRoute,
});

/**
 * TikTokは1つのTT4Dアプリを全アカウントで共有し、どのTikTokアカウントへ繋がるかはブラウザの
 * ログイン状態だけで決まる。そのため「連携済み」として扱えるアカウントは常に1つだけで、別の
 * アカウントで連携し直すと前のアカウントの連携済み表示は外す必要がある。連携済みのaccountIdを
 * 1つだけ持つことでこれを表現する（`connectedAccountId === accountId` のタブにだけバッジを出し、
 * 他のタブには連携ボタンを残す）。
 *
 * この状態と `connectingAccountIdRef` を子ではなくこのルートに置くのは、下の
 * `key={accountId}` で子コンポーネントがタブ切り替えごとに作り直されるため。
 * @graph-connects none
 */
export function SnsContentGeneratorAccountRoute() {
  const { t } = useTranslation();
  const { accountId } = Route.useParams();
  const [connectedAccountId, setConnectedAccountId] = useState<string | null>(null);
  // 認証ポップアップを開いている間にタブを切り替えても、連携済みになるのはOAuthを開始した
  // アカウントのため、開始時のaccountIdを覚えておく。
  const connectingAccountIdRef = useRef<string | null>(null);

  /** @graph-connects none */
  const handleTiktokConnectStarted = useCallback((startedAccountId: string) => {
    connectingAccountIdRef.current = startedAccountId;
  }, []);

  // 子のBroadcastChannel購読がこの関数をdepsに持つため、毎レンダーで作り直すと購読を
  // 張り直してしまう。参照を固定して購読を張ったままにする。
  /** @graph-connects none */
  const handleTiktokAccountConnected = useCallback(() => {
    setConnectedAccountId(connectingAccountIdRef.current);
  }, []);

  if (!isKnownSnsContentGeneratorAccountId(accountId)) {
    return (
      <ErrorState
        title={t('snsContentGenerator.unknownAccountTitle')}
        message={t('snsContentGenerator.unknownAccountMessage')}
      />
    );
  }

  // アカウント切り替え（タブ遷移）ではルートのparamsだけが変わり、TanStack Routerは route
  // component を再マウントしない。`key={accountId}` を付けないと、生成途中のstate（テーマ・
  // タイトル・生成済み画像・投稿文等）や画像生成枚数の初期値が別アカウントのタブへ残り、
  // アカウントAで生成した画像をaccountId=BとしてTikTokへ送ってしまう。
  return (
    <SnsContentGeneratorIndexPage
      key={accountId}
      accountId={accountId}
      isTiktokAccountConnected={connectedAccountId === accountId}
      onTiktokConnectStarted={handleTiktokConnectStarted}
      onTiktokAccountConnected={handleTiktokAccountConnected}
    />
  );
}

export interface SnsContentGeneratorIndexPageProps {
  /** 表示・生成対象のアカウントID。呼び出し元（ルート）がURLの `$accountId` を検証済みの値として渡す */
  accountId: string;
  /** このアカウントのタブがTikTok連携済みとして表示されるかどうか。判定は呼び出し元（ルート）が持つ */
  isTiktokAccountConnected: boolean;
  /** TikTok連携（OAuth）を開始したことを呼び出し元へ伝える。どのアカウント向けの連携かを記録させる */
  onTiktokConnectStarted: (accountId: string) => void;
  /** TikTok連携が完了したことを呼び出し元へ伝える。連携済み表示の対象アカウントを更新させる */
  onTiktokAccountConnected: () => void;
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business SNSコンテンツ生成の1画面構成。テーマ・タイトルの生成・編集、行ごとまたは一括での画像生成対象への追加、画像生成・ダウンロードまでを1画面内で完結させる
 * @graph-connects ThemeTitleEditor [calls] 編集可能リストコンポーネント描画
 * @graph-connects useThemeTitleRows [calls] 編集行のstate管理フック呼び出し
 * @graph-connects useConfirmedThemeTitles [calls] 画像生成対象リストのstate管理フック呼び出し
 * @graph-connects useGenerateContentImages [calls] 画像生成ミューテーション呼び出し
 * @graph-connects useGenerateCaption [calls] ハッシュタグ・説明文生成ミューテーション呼び出し
 * @graph-connects useConnectTiktok [calls] TikTok下書き送信ミューテーション呼び出し
 * @graph-connects useTiktokAuthUrl [calls] TikTok連携の認証URL発行ミューテーション呼び出し
 * @graph-connects GeneratedImageGallery [calls] 画像生成結果コンポーネント描画
 */
export function SnsContentGeneratorIndexPage({
  accountId,
  isTiktokAccountConnected,
  onTiktokConnectStarted,
  onTiktokAccountConnected,
}: SnsContentGeneratorIndexPageProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const {
    rows,
    addManualRow,
    removeRow,
    updateTheme,
    updateTitle,
    appendGenerated,
    markRowExecuted,
    markRowsExecuted,
  } = useThemeTitleRows();
  const { confirmedThemeTitles, appendConfirmedThemeTitles, removeConfirmedThemeTitle } =
    useConfirmedThemeTitles();
  const [generatedImages, setGeneratedImages] = useState<GeneratedContentImages[]>([]);
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());
  const [imagePageCount, setImagePageCount] = useState(getDefaultImagePageCount(accountId));
  const generateImagesMutation = useGenerateContentImages();

  const [captionTexts, setCaptionTexts] = useState<Map<string, string>>(new Map());
  const [generatingCaptionIds, setGeneratingCaptionIds] = useState<Set<string>>(new Set());
  const generateCaptionMutation = useGenerateCaption();

  const [connectedTiktokIds, setConnectedTiktokIds] = useState<Set<string>>(new Set());
  const [connectingTiktokIds, setConnectingTiktokIds] = useState<Set<string>>(new Set());
  const connectTiktokMutation = useConnectTiktok();
  const tiktokAuthUrlMutation = useTiktokAuthUrl();

  // 生成途中のstate（テーマ・タイトル・生成済み画像・投稿文等）は、呼び出し元（ルート）が
  // `key={accountId}` を付けてこのコンポーネントごと作り直すことでアカウント切り替え時に
  // リセットされる。切り替え時にstateを引き継ぐための特別な処理は行わない。

  const existingThemes = rows
    .map((row) => normalizeThemeTitleText(row.theme))
    .filter((theme) => theme !== '');
  const canExecuteAll = rows.some(
    (row) => !row.isExecuted && row.theme.trim() !== '' && row.title.trim() !== '',
  );
  const generatedIds = new Set(generatedImages.map((image) => image.id));
  const pendingForImageGeneration = confirmedThemeTitles.filter(
    (item) => !generatedIds.has(item.id),
  );
  const canGenerateImages = pendingForImageGeneration.length > 0;

  const handleGenerated = (items: GeneratedThemeTitle[]) => {
    appendGenerated(items);
    showToast('success', t('snsContentGenerator.generateSuccess', { count: items.length }), 3000);
  };

  const handleGenerateError = (message: string) => {
    showToast('error', message, 5000);
  };

  const handleExecuteRow = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    const item = toConfirmedThemeTitle(row);
    if (item === null) return;
    appendConfirmedThemeTitles([item]);
    markRowExecuted(id);
  };

  const handleExecuteAll = () => {
    const targets = rows.filter((row) => !row.isExecuted);
    const items = toConfirmedThemeTitles(targets);
    if (items.length === 0) return;
    appendConfirmedThemeTitles(items);
    markRowsExecuted(items.map((item) => item.id));
  };

  /**
   * ハッシュタグ・説明文を1件生成し、投稿文と結合した編集可能テキストとして captionTexts に積む。
   * `/captions` は theme/title しか使わないため画像生成の完了を待つ必要がなく、画像生成の開始と
   * 同時に呼べる（`generateImagesFor` の `onItemStart` から呼ばれる）。GeneratedImageGallery の
   * 手動ボタン（生成失敗時の再試行用）からも呼ばれる共通処理。失敗しても画像側の結果は
   * 一切巻き添えにしない。
   */
  const generateCaptionFor = (item: { id: string; theme: string; title: string }) => {
    setGeneratingCaptionIds((prev) => new Set([...prev, item.id]));
    generateCaptionMutation.mutate(
      { accountId, theme: item.theme, title: item.title },
      {
        onSuccess: (caption) => {
          setCaptionTexts((prev) =>
            new Map(prev).set(item.id, combineCaptionText(caption.postText, caption.hashtags)),
          );
        },
        onError: () => {
          showToast('error', t('snsContentGenerator.generateCaptionError'), 5000);
        },
        onSettled: () => {
          setGeneratingCaptionIds((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        },
      },
    );
  };

  /** 指定したアイテムを画像生成中として記録し、完了後に解除する共通処理。
   * 画像はページ単位で逐次生成されるため、onItemUpdate で届いた時点のスナップショットで
   * generatedImages を都度置き換える（未登録なら追加、登録済みなら更新）。
   * アイテムごとの画像生成（`generatePagePrompts`）を始める直前に、続けてハッシュタグ・説明文の
   * 生成も同時に開始する（「画像・投稿文を生成」ボタン1つで両方を実行する。画像生成の完了を
   * 待たない）。アイテムは順番に処理されるため、Python側の同時実行数上限
   * （`maxInstanceRequestConcurrency: 4` = 画像3並列 + キャプション1）を超えない。 */
  const generateImagesFor = (targets: ConfirmedThemeTitle[]) => {
    if (targets.length === 0) return;
    const targetIds = targets.map((item) => item.id);
    setGeneratingIds((prev) => new Set([...prev, ...targetIds]));
    generateImagesMutation.mutate(
      {
        accountId,
        items: targets,
        pageCount: imagePageCount,
        onItemStart: generateCaptionFor,
        onItemUpdate: (image) => {
          setGeneratedImages((prev) => {
            const index = prev.findIndex((existing) => existing.id === image.id);
            if (index === -1) return [...prev, image];
            const next = [...prev];
            next[index] = image;
            return next;
          });
        },
      },
      {
        onSettled: () => {
          setGeneratingIds((prev) => {
            const next = new Set(prev);
            targetIds.forEach((id) => next.delete(id));
            return next;
          });
        },
      },
    );
  };

  const handleGenerateImages = () => {
    generateImagesFor(pendingForImageGeneration);
  };

  const handleGenerateImageForItem = (item: ConfirmedThemeTitle) => {
    generateImagesFor([item]);
  };

  const handleRemoveConfirmedItem = (id: string) => {
    removeConfirmedThemeTitle(id);
  };

  const handleGenerateCaption = (item: GeneratedContentImages) => {
    generateCaptionFor(item);
  };

  const handleCaptionTextChange = (id: string, text: string) => {
    setCaptionTexts((prev) => new Map(prev).set(id, text));
  };

  const handleConnectTiktok = (item: GeneratedContentImages) => {
    const description = captionTexts.get(item.id);
    if (description === undefined) return;
    setConnectingTiktokIds((prev) => new Set([...prev, item.id]));
    connectTiktokMutation.mutate(
      { accountId, contentId: item.contentId, title: item.title, description },
      {
        onSuccess: () => {
          setConnectedTiktokIds((prev) => new Set([...prev, item.id]));
          showToast(
            'success',
            t('snsContentGenerator.tiktokConnectSuccess', { theme: item.theme }),
            3000,
          );
        },
        onError: () => {
          showToast('error', t('snsContentGenerator.tiktokConnectError'), 5000);
        },
        onSettled: () => {
          setConnectingTiktokIds((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        },
      },
    );
  };

  /**
   * `/sns-content-generator/tiktok/callback` からの `BroadcastChannel` 通知（連携結果）を受け取る。
   * （`window.opener` 経由の `postMessage` は使わない。`handleConnectTiktokAccount` 側で reverse
   * tabnabbing 対策として `popup.opener = null` を設定しているため、ポップアップ側からは元タブへの
   * `opener` 参照が無く、`postMessage` で通知できない）。
   *
   * **`window.location.reload()` は絶対に使わない。** テーマ・タイトル・生成済み画像・投稿文は
   * すべて React state のみで保持しており（`useConfirmedThemeTitles` は sessionStorage 等へ
   * 永続化していない）、リロードすると生成に使った時間と費用がかかったコンテンツが全て消える。
   * そのため更新は `onTiktokAccountConnected()` の呼び出し（呼び出し元が持つ連携済み accountId の
   * 更新）だけに留め、他の state（generatedImages / captionTexts / confirmedThemeTitles 等）には
   * 一切触れない。
   */
  useEffect(() => {
    const channel = new BroadcastChannel(TIKTOK_CONNECT_WINDOW_NAME);
    /** @graph-connects none */
    function handleTiktokCallbackMessage(event: MessageEvent<TiktokCallbackMessage>) {
      const data = event.data;
      if (data?.type !== 'tiktok-callback') return;
      if (data.status === 'success') {
        onTiktokAccountConnected();
        showToast('success', t('snsContentGenerator.tiktokAccountConnectSuccess'), 3000);
      } else {
        showToast('error', t('snsContentGenerator.tiktokAccountConnectError'), 5000);
      }
    }
    channel.addEventListener('message', handleTiktokCallbackMessage);
    return () => {
      channel.removeEventListener('message', handleTiktokCallbackMessage);
      channel.close();
    };
  }, [onTiktokAccountConnected, showToast, t]);

  /**
   * TikTokアカウント連携（OAuth）を開始する。組織で1つのTikTokアカウントを最初に連携するときだけ
   * 使う想定で、以降はCloud Schedulerが1日1回トークンをリフレッシュし続ける。
   *
   * 認証画面は別タブ（ポップアップ）で開く。同じタブで遷移すると作業中の入力（テーマ・タイトル・
   * 生成済み画像等はReact stateのみで永続化していない）が失われるため。
   *
   * ポップアップは `authorizeUrl` 取得（非同期の fetch）を待たず、クリックハンドラの同期呼び出し
   * スタック内で `about:blank` を対象に開く。認証URL取得後に開こうとすると、ブラウザ（特に
   * Safari系）がユーザー操作起因のポップアップと認識せずブロック対象にしてしまうため。
   *
   * 開いた直後（`about:blank` でまだ同一オリジンの間）に `popup.opener = null` を設定してから
   * `authorizeUrl`（TikTok側の外部ドメイン）へ遷移させる。これにより、ポップアップ側
   * （TikTokの認証画面）から `window.opener.location` を書き換えて元タブをフィッシングサイト等へ
   * 誘導する reverse tabnabbing を防ぐ（`window.open` に `noopener` を渡すと戻り値が常に `null`
   * になり `popup.location.href = authorizeUrl` で遷移させられなくなるため、`noopener` は使わず
   * このパターンで同等の効果を得る）。`popup.opener` を切り離すため、連携結果の通知は
   * `window.opener` 経由の `postMessage` ではなく `BroadcastChannel`（上の useEffect）で行う。
   *
   * ポップアップブロックで `window.open` が失敗した場合は、従来どおり同じタブでの遷移に
   * フォールバックする。
   */
  const handleConnectTiktokAccount = () => {
    // ポップアップを開いている間にタブを切り替えても連携先が分かるよう、開始時点で記録しておく。
    onTiktokConnectStarted(accountId);
    const popup = window.open('about:blank', TIKTOK_CONNECT_WINDOW_NAME, 'width=500,height=720');
    if (popup) {
      popup.opener = null;
    }
    tiktokAuthUrlMutation.mutate(accountId, {
      onSuccess: ({ authorizeUrl }) => {
        // ポップアップブロッカーに阻まれた場合、popupはnull、または開いた直後にclosedがtrueになる
        // （ブラウザによってはclosedがすぐには反映されずundefinedのままのこともあるため両方見る）。
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          window.location.href = authorizeUrl;
          return;
        }
        popup.location.href = authorizeUrl;
      },
      onError: () => {
        // 認証URLの取得に失敗した場合、同期的に開いておいた about:blank のポップアップが
        // 空のまま残ってしまうため閉じる。
        popup?.close();
        showToast('error', t('snsContentGenerator.tiktokAuthUrlError'), 5000);
      },
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="animate-content-reveal flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('snsContentGenerator.title')}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('snsContentGenerator.description')}
          </p>
        </div>
        {isTiktokAccountConnected ? (
          <Badge variant="success" size="sm">
            {t('snsContentGenerator.tiktokAccountConnected')}
          </Badge>
        ) : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleConnectTiktokAccount}
            loading={tiktokAuthUrlMutation.isPending}
          >
            {t('snsContentGenerator.tiktokConnectAccount')}
          </Button>
        )}
      </div>

      <ThemeTitleEditor
        accountId={accountId}
        rows={rows}
        onAddManualRow={addManualRow}
        onRemoveRow={removeRow}
        onUpdateTheme={updateTheme}
        onUpdateTitle={updateTitle}
        existingThemes={existingThemes}
        onGenerated={handleGenerated}
        onGenerateError={handleGenerateError}
        onExecuteRow={handleExecuteRow}
      />

      <div className="flex flex-col items-end gap-2">
        {!canExecuteAll && (
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('snsContentGenerator.executeDisabledHint')}
          </p>
        )}
        <Button
          type="button"
          variant="primary"
          onClick={handleExecuteAll}
          disabled={!canExecuteAll}
        >
          {t('snsContentGenerator.execute')}
        </Button>
      </div>

      <div className="space-y-3 pt-4 border-t border-[var(--color-border)]">
        <div>
          <h2 className="text-lg font-bold text-[var(--color-text-primary)]">
            {t('snsContentGenerator.confirmTitle')}
          </h2>
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('snsContentGenerator.confirmDescription')}
          </p>
        </div>

        <ThemeTitleConfirmList
          items={confirmedThemeTitles}
          onGenerateImage={handleGenerateImageForItem}
          generatingIds={generatingIds}
          generatingCaptionIds={generatingCaptionIds}
          generatedIds={generatedIds}
          onRemoveItem={handleRemoveConfirmedItem}
        />

        <div className="flex items-end justify-between gap-4">
          <div className="w-32">
            <Input
              type="number"
              label={t('snsContentGenerator.imagePageCountLabel')}
              value={imagePageCount}
              min={MIN_IMAGE_PAGE_COUNT}
              max={MAX_IMAGE_PAGE_COUNT}
              size="sm"
              onChange={(e) => {
                const parsed = Number(e.target.value);
                if (Number.isNaN(parsed)) return;
                const clamped = Math.min(
                  MAX_IMAGE_PAGE_COUNT,
                  Math.max(MIN_IMAGE_PAGE_COUNT, parsed),
                );
                setImagePageCount(clamped);
              }}
            />
          </div>
          <Button
            type="button"
            variant="primary"
            onClick={handleGenerateImages}
            disabled={!canGenerateImages || generatingIds.size > 0}
            loading={generatingIds.size > 0}
          >
            {generatingIds.size > 0
              ? t('snsContentGenerator.generatingImages')
              : t('snsContentGenerator.proceedToImage')}
          </Button>
        </div>
      </div>

      {generatedImages.length > 0 && (
        <div className="space-y-3 pt-4 border-t border-[var(--color-border)]">
          <h2 className="text-lg font-bold text-[var(--color-text-primary)]">
            {t('snsContentGenerator.generatedImagesTitle')}
          </h2>
          <GeneratedImageGallery
            accountId={accountId}
            items={generatedImages}
            captionTexts={captionTexts}
            generatingCaptionIds={generatingCaptionIds}
            onGenerateCaption={handleGenerateCaption}
            onCaptionTextChange={handleCaptionTextChange}
            connectedTiktokIds={connectedTiktokIds}
            connectingTiktokIds={connectingTiktokIds}
            onConnectTiktok={handleConnectTiktok}
          />
        </div>
      )}
    </div>
  );
}
